import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import * as SnmpAlertActions from '@modules/snmp-custom-notifications/store/alert/actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { DataChangeWatcherService, DataPageBase } from 'mbs-ui-kit';
import { Observable, debounceTime, filter, tap } from 'rxjs';
import { recipientValidator } from 'mbs-ui-shared/src/lib/utils/recipient-validator';
import {
  CustomAlertingDefaultValues,
  IncludeServices,
  NotifyTimeTypeTranslationKeys,
  RMMAlertTypes,
  RmmAlertingGeneralAlerts,
  RmmAlertingWindowsAlerts
} from 'mbs-ui-shared@models/rmm-custom-notification-model';
import { pushRecipientValidator } from 'mbs-ui-shared@utils/push-recipient-validator';
import { RmmCustomAlertingServiceController } from '../../services/rmm-custom-alerting-controller.service';

@UntilDestroy()
@Component({
  selector: 'mbs-rmm-custom-alert-full',
  templateUrl: './rmm-custom-alert-full.component.html'
})
export class RmmCustomAlertFull extends DataPageBase implements OnInit, OnDestroy {
  /*
   * showInOneRow
   * if true - layout should present all cards in one row with vertical orientation
   * */

  @Input() showInOneRow = false;

  /*
   * usingSwitch
   * if true - show on/off triggers
   * if false - show only enabled alerts with disable button
   * */
  @Input() usingSwitch = true;
  @Input() disableSettingChanges = false;
  @Input() disableChangeDetector = false;

  /*
   * dataChange
   * return actual settings on customSettingsForm value change if required
   * */
  @Output() dataChange = new EventEmitter<any>();

  private readonly minutesTimeType = this.i18next.transform(NotifyTimeTypeTranslationKeys.Minutes);
  private readonly hoursTimeType = this.i18next.transform(NotifyTimeTypeTranslationKeys.Hours);
  public alertToBeShown: RMMAlertTypes[] = RmmAlertingGeneralAlerts.concat(RmmAlertingWindowsAlerts);
  public rmmAlertTypes = RMMAlertTypes;
  public notifyItems = [this.hoursTimeType, this.minutesTimeType];

  public customSettingsForm = new UntypedFormGroup({
    enabled: new FormControl(false)
  });

  private readonly withTimeDurationTypes = [
    RMMAlertTypes.HighCpuUsage,
    RMMAlertTypes.HighMemoryUsage,
    RMMAlertTypes.LowDiskSpace,
    RMMAlertTypes.NoAntivirus,
    RMMAlertTypes.NotUpdated,
    RMMAlertTypes.SmartNotHealthy,
    RMMAlertTypes.ConnectionLoss,
    RMMAlertTypes.HighTemperature,
    RMMAlertTypes.ServiceState,
    RMMAlertTypes.WrongSystemTime
  ];

  private readonly extendedParamTypes = ['updateDeferNumber', 'updateDeferType', 'updateDeferNumberDiff', 'updateDeferTypeDiff'];

  private readonly minMinutesTime = 5;
  private readonly maxHoursTime = 48;
  public readonly cardDefaultWidth = '540px';

  public notificationForm = new UntypedFormGroup({
    recipient: new FormControl({ rule: 0, emails: [] }, recipientValidator.bind(this)),
    pushRecipient: new FormControl({ type: 0, users: [] }, pushRecipientValidator.bind(this)),
    pushNotification: new UntypedFormGroup({
      enabled: new FormControl(false)
    }),
    [RMMAlertTypes.HighCpuUsage]: new UntypedFormGroup({
      enabled: new FormControl(false),
      alertPercent: new FormControl(CustomAlertingDefaultValues.alertPercent.ninety),
      duration: new FormControl(CustomAlertingDefaultValues.duration.five),
      updateDeferNumber: new FormControl(),
      updateDeferType: new FormControl(this.minutesTimeType)
    }),
    [RMMAlertTypes.HighMemoryUsage]: new UntypedFormGroup({
      enabled: new FormControl(false),
      alertPercent: new FormControl(CustomAlertingDefaultValues.alertPercent.ninety),
      duration: new FormControl(CustomAlertingDefaultValues.duration.five),
      updateDeferNumber: new FormControl(),
      updateDeferType: new FormControl(this.minutesTimeType)
    }),
    [RMMAlertTypes.LowDiskSpace]: new UntypedFormGroup({
      enabled: new FormControl(false),
      alertPercent: new FormControl(CustomAlertingDefaultValues.alertPercent.ninety),
      //
      duration: new FormControl(CustomAlertingDefaultValues.duration.five),
      updateDeferNumber: new FormControl(),
      updateDeferType: new FormControl(this.minutesTimeType),
      //
      excludedVolumeLabels: new FormControl([].join(', ')),
      excludedMountPoints: new FormControl([].join(', ')),
      excludeBootVolume: new FormControl(false),
      excludeRemovableDisks: new FormControl(false)
    }),
    [RMMAlertTypes.NoAntivirus]: new UntypedFormGroup({
      enabled: new FormControl(false),
      duration: new FormControl(CustomAlertingDefaultValues.duration.fifteen),
      updateDeferNumber: new FormControl(),
      updateDeferType: new FormControl(this.minutesTimeType)
    }),
    [RMMAlertTypes.NotUpdated]: new UntypedFormGroup({
      enabled: new FormControl(false),
      duration: new FormControl(CustomAlertingDefaultValues.duration.five),
      updateDeferNumber: new FormControl(),
      updateDeferType: new FormControl(this.minutesTimeType)
    }),
    [RMMAlertTypes.SmartNotHealthy]: new UntypedFormGroup({
      enabled: new FormControl(false),
      duration: new FormControl(CustomAlertingDefaultValues.duration.five),
      updateDeferNumber: new FormControl(),
      updateDeferType: new FormControl(this.minutesTimeType)
    }),
    [RMMAlertTypes.ConnectionLoss]: new UntypedFormGroup({
      enabled: new FormControl(false),
      notifyToPlatform: new FormControl(false),
      notifyOnRestore: new FormControl(false),
      //
      duration: new FormControl(CustomAlertingDefaultValues.duration.twenty),
      updateDeferNumber: new FormControl(),
      updateDeferType: new FormControl(this.minutesTimeType)
    }),
    [RMMAlertTypes.SnmpDevice]: new UntypedFormGroup({
      enabled: new FormControl(false),
      alerts: new FormControl([])
    }),
    [RMMAlertTypes.SoftwareChanges]: new UntypedFormGroup({
      enabled: new FormControl(false),
      excludedNames: new FormControl([].join(', ')),
      excludedVendor: new FormControl([].join(', '))
    }),
    [RMMAlertTypes.HardwareChanges]: new UntypedFormGroup({
      enabled: new FormControl(false),
      excludedNames: new FormControl(CustomAlertingDefaultValues.hardwareChanges.excludedNames.join(', ')),
      excludedPNPClasses: new FormControl(CustomAlertingDefaultValues.hardwareChanges.excludedPNPClasses.join(', ')),
      excludedPNPDeviceIDs: new FormControl(CustomAlertingDefaultValues.hardwareChanges.excludedPNPDeviceIDs.join(', ')),
      excludedManufacturer: new FormControl([].join(', '))
    }),
    [RMMAlertTypes.HighTemperature]: new UntypedFormGroup({
      enabled: new FormControl(false),
      alertHighTemperature: new FormControl(CustomAlertingDefaultValues.alertPercent.eighty),
      duration: new FormControl(CustomAlertingDefaultValues.duration.five),

      updateDeferNumber: new FormControl(),
      updateDeferType: new FormControl(this.minutesTimeType)
    }),
    [RMMAlertTypes.WrongSystemTime]: new UntypedFormGroup({
      enabled: new FormControl(false),
      alertWrongSystemTimeDifference: new FormControl(CustomAlertingDefaultValues.duration.five),
      updateDeferNumberDiff: new FormControl(),
      updateDeferTypeDiff: new FormControl(this.minutesTimeType)
    }),
    [RMMAlertTypes.ServiceState]: new UntypedFormGroup({
      enabled: new FormControl(false),
      includeServices: new FormControl([]),
      duration: new FormControl(CustomAlertingDefaultValues.duration.five),

      updateDeferNumber: new FormControl(),
      updateDeferType: new FormControl(this.minutesTimeType)
    })
  });

  constructor(
    private rmmCustomAlertingService: RmmCustomAlertingServiceController,
    private store: Store,
    private i18next: I18NextPipe,
    dcw: DataChangeWatcherService
  ) {
    super(dcw);

    this.rmmCustomAlertingService.dataHasBeingChanged.pipe(untilDestroyed(this)).subscribe((value) => {
      if (!value && !this.disableChangeDetector) this.attachObject(this.notificationForm, (obj: UntypedFormGroup) => obj.getRawValue());
    });

    this.rmmCustomAlertingService.usedSettings
      .pipe(
        debounceTime(200),
        filter((value) => !!value),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        this.notificationForm.patchValue(cloneDeep(value), { emitEvent: false });

        this.store.dispatch(SnmpAlertActions.setSnmpAlertCustomisation({ data: value?.snmpDevice?.alerts ?? [] }));
        this.withTimeDurationTypes.forEach((type) => this.setupDurationFormUIValues(type));

        if (!this.rmmCustomAlertingService.dataHasBeingChanged.getValue() && !this.disableChangeDetector) {
          this.attachObject(this.notificationForm, (obj: UntypedFormGroup) => obj.getRawValue());
        }
      });
  }

  ngOnInit(): void {
    if (this.disableChangeDetector) this.canDiscardWithChanges = true;
    this.withTimeDurationTypes.forEach((type) => this.startOnDurationFormChangesCheck(type));

    this.notificationForm.valueChanges.pipe(untilDestroyed(this)).subscribe((settings) => {
      const newSettings = cloneDeep(settings);

      this.withTimeDurationTypes.forEach((key) => {
        this.extendedParamTypes.forEach((subKey) => {
          if (newSettings[key][subKey]) delete newSettings[key][subKey];
        });
      });

      this.rmmCustomAlertingService.syncCurrentData(newSettings);
      this.dataChange.emit(newSettings);
    });
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  isValidSidepanel(): boolean {
    return true;
  }

  public handleSave(): Observable<boolean> {
    return this.rmmCustomAlertingService.getSaveObservable();
  }

  disableAlert(type: RMMAlertTypes) {
    this.notificationForm.get(type).get('enabled').setValue(false);
  }

  // on form duration change subscription
  private startOnDurationFormChangesCheck(key: RMMAlertTypes): void {
    if (key === RMMAlertTypes.WrongSystemTime) {
      this.parseAndSetTimeOnFormValueChange(key, 'updateDeferNumberDiff', 'updateDeferTypeDiff');
    } else {
      this.parseAndSetTimeOnFormValueChange(key, 'updateDeferNumber', 'updateDeferType');
    }
  }

  parseAndSetTimeOnFormValueChange(key: RMMAlertTypes, timeNumberControlName: string, timeTypeControlName: string): void {
    const timeNumberForm = this.notificationForm.get(key).get(timeNumberControlName);
    const timeTypeForm = this.notificationForm.get(key).get(timeTypeControlName);

    timeNumberForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value: number) => this.onFormDurationChange(value, timeTypeForm.value, key));

    timeTypeForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value: string) => {
      if (timeTypeForm.value === this.hoursTimeType && timeNumberForm.value > this.maxHoursTime)
        timeNumberForm.setValue(this.maxHoursTime, { emitEvent: false });
      if (timeTypeForm.value === this.minutesTimeType && timeNumberForm.value < this.minMinutesTime)
        timeNumberForm.setValue(this.getMinimumTimeValueByKey(key), { emitEvent: false });

      this.onFormDurationChange(timeNumberForm.value, value, key);
    });
  }

  private getMinimumTimeValueByKey(key: RMMAlertTypes) {
    switch (key) {
      case RMMAlertTypes.ConnectionLoss:
        return 20;
      case RMMAlertTypes.NoAntivirus:
        return 15;
      default:
        return 5;
    }
  }

  private onFormDurationChange(timeNumber: number, timeType: string, formGroupName: string, formControlName = 'duration'): void {
    this.notificationForm
      .get(formGroupName)
      .get(formGroupName === RMMAlertTypes.WrongSystemTime ? 'alertWrongSystemTimeDifference' : formControlName)
      .setValue(this.getDurationValueInServerFormat(timeNumber, timeType), { emitEvent: false });
  }

  private getDurationValueInServerFormat(timeNumber: number, timeType: string): string {
    const days = timeType === this.hoursTimeType ? Math.floor(timeNumber / 24) : null;
    const hours = timeType === this.hoursTimeType ? this.getNumberWithZeroOnStartIfLessThenTen(timeNumber % 24) : '00';
    const minutes = timeType === this.minutesTimeType ? this.getNumberWithZeroOnStartIfLessThenTen(timeNumber) : '00';
    const seconds = '00';

    return `${days ? days + '.' : ''}${hours}:${minutes}:${seconds}`;
  }

  private getNumberWithZeroOnStartIfLessThenTen(value: number): string {
    if (value < 10) return `0${value}`;
    return `${value}`;
  }

  // parse recieved duration time and change it in form
  private setupDurationFormUIValues(key: RMMAlertTypes): void {
    if (key === RMMAlertTypes.WrongSystemTime) {
      const { valueOfNumber, valueOfType } = this.getValueOfTypeAndNumberFromServerValue(
        this.notificationForm.get(key).get('alertWrongSystemTimeDifference').value
      );
      this.setFormValue(key, 'updateDeferNumberDiff', valueOfNumber, 'updateDeferTypeDiff', valueOfType);
    } else {
      const { valueOfNumber, valueOfType } = this.getValueOfTypeAndNumberFromServerValue(
        this.notificationForm.get(key).get('duration').value
      );
      this.setFormValue(key, 'updateDeferNumber', valueOfNumber, 'updateDeferType', valueOfType);
    }
  }

  private getValueOfTypeAndNumberFromServerValue(value: string) {
    const hmsSplitter = ':';
    const daysSplitter = '.';

    if (!value) return { valueOfNumber: null, valueOfType: this.minutesTimeType };

    const [hours, minutes, seconds] = value.split(hmsSplitter);
    if (hours.includes(daysSplitter)) {
      const [days, newHours] = hours.split(daysSplitter);

      return { valueOfNumber: Number(days) * 24 + Number(newHours), valueOfType: this.hoursTimeType };
    }

    if (Number(hours) > 0) return { valueOfNumber: Number(hours), valueOfType: this.hoursTimeType };
    if (Number(minutes) > 0) return { valueOfNumber: Number(minutes), valueOfType: this.minutesTimeType };
    return { valueOfNumber: 15, valueOfType: this.minutesTimeType };
  }

  private setFormValue(formAlertType: string, keyOfNumber: string, valueOfNumber: number, keyOfType: string, valueOfType: string) {
    this.notificationForm.get(formAlertType).get(keyOfNumber).setValue(valueOfNumber, { emitEvent: false });
    this.notificationForm.get(formAlertType).get(keyOfType).setValue(valueOfType, { emitEvent: false });
  }

  onServiceSelectionChanged(selectedServices: IncludeServices): void {
    this.notificationForm.get(RMMAlertTypes.ServiceState).get('includeServices').setValue(selectedServices);
  }

  onSnmpSelectionChange(alerts: string[]) {
    this.notificationForm.get(RMMAlertTypes.SnmpDevice).get('alerts').setValue(alerts);
  }
}
